import React, { useState } from "react";
import { Upload as UploadIcon, FileCheck2, CloudDownload, Info, RefreshCw, ShieldAlert, Database, ServerCrash, Activity } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { uploadCsv, uploadConstituents, autoFetchBhavcopy, autoFetchIndices } from "../lib/api"; 

export default function UploadPage() {
  const [csvFile, setCsvFile] = useState(null);
  const [csvDate, setCsvDate] = useState("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvResult, setCsvResult] = useState(null);

  const [idxFile, setIdxFile] = useState(null);
  const [idxName, setIdxName] = useState("");
  const [idxBusy, setIdxBusy] = useState(false);
  const [idxResult, setIdxResult] = useState(null);

  const [fetchStartDate, setFetchStartDate] = useState("");
  const [fetchEndDate, setFetchEndDate] = useState("");
  const [fetchBusy, setFetchBusy] = useState(false);
  const [fetchResults, setFetchResults] = useState(null);

  const [autoIdxBusy, setAutoIdxBusy] = useState(false);

  const handleCsv = async (e) => {
    e.preventDefault();
    if (!csvFile) return toast.error("Choose a Bhavcopy CSV file");
    setCsvBusy(true);
    try {
      const { data } = await uploadCsv(csvFile, csvDate || undefined);
      setCsvResult(data);
      toast.success(`Imported ${data.stocks_upserted} stocks, ${data.indices_upserted} indices`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } 
    finally { setCsvBusy(false); }
  };

  const handleIdx = async (e) => {
    e.preventDefault();
    if (!idxFile || !idxName) return toast.error("Index name and CSV required");
    setIdxBusy(true);
    try {
      const { data } = await uploadConstituents(idxName, idxFile);
      setIdxResult(data);
      toast.success(`${idxName}: ${data.constituents_upserted} constituents saved`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); } 
    finally { setIdxBusy(false); }
  };

  const handleAutoFetchBhavcopy = async (e) => {
    e.preventDefault();
    setFetchBusy(true); setFetchResults(null);
    try {
      const { data } = await autoFetchBhavcopy(fetchStartDate || undefined, fetchEndDate || undefined);
      setFetchResults(data.details || []);
      toast.success("Auto-fetch process completed");
    } catch (err) { toast.error(err?.response?.data?.message || err?.message || "Auto-fetch failed"); } 
    finally { setFetchBusy(false); }
  };

  const handleAutoFetchIndices = async () => {
    setAutoIdxBusy(true);
    try {
      const { data } = await autoFetchIndices();
      toast.success(`Success! ${data.total_upserted} total symbols synced.`);
      setIdxResult({ index_name: "All Major Indices", constituents_upserted: data.total_upserted });
    } catch (err) { toast.error("Auto-fetch failed. NSE API might be blocking or server error."); } 
    finally { setAutoIdxBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-6 pb-12 px-4 md:px-8">
      <div className="max-w-[1200px] mx-auto">
        
        {/* 🔥 ADMIN HEADER 🔥 */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-slate-900 p-6 rounded-2xl shadow-xl">
          <div className="flex items-center gap-4">
             <div className="bg-sky-500/20 p-3 rounded-xl border border-sky-500/30">
               <Database className="w-8 h-8 text-sky-400" />
             </div>
             <div>
               <h1 className="text-2xl font-black tracking-tight text-white flex items-center gap-2">
                 DATA CONTROL CENTER
                 <span className="bg-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold tracking-widest uppercase flex items-center gap-1 shadow-[0_0_10px_rgba(244,63,94,0.5)]"><ShieldAlert className="w-3 h-3"/> Admin Privileges Active</span>
               </h1>
               <p className="text-slate-400 text-sm font-medium mt-1">Manage global data pipelines, NSE syncs, and manual overrides.</p>
             </div>
          </div>
          <div className="flex items-center gap-3 bg-slate-800 px-4 py-2 rounded-lg border border-slate-700">
             <Activity className="w-4 h-4 text-emerald-400 animate-pulse" />
             <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">Server Status: Online</span>
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* 🔥 LEFT COLUMN: AUTO FETCH ENGINE 🔥 */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-0 overflow-hidden border-sky-200 shadow-lg bg-white rounded-2xl">
              <div className="bg-gradient-to-r from-sky-600 to-blue-700 p-5 flex items-center justify-between">
                 <div className="flex items-center gap-3 text-white">
                   <CloudDownload className="w-6 h-6" />
                   <h2 className="text-xl font-black tracking-tight">NSE Auto-Fetch Engine</h2>
                 </div>
                 <span className="text-[10px] bg-white/20 text-white px-2 py-1 rounded font-bold uppercase tracking-wider">Primary Data Source</span>
              </div>
              
              <div className="p-6">
                <p className="text-sm text-slate-600 mb-6 bg-sky-50 p-3 rounded-lg border border-sky-100 flex items-start gap-3">
                  <Info className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" />
                  <span>Downloads PR.zip directly from NSE servers, extracts pdDDMMYYYY.csv, and pipes it into the database. Leave dates empty to trigger today's live sync.</span>
                </p>
                
                <form onSubmit={handleAutoFetchBhavcopy} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <Label htmlFor="fetch-start-date" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Historical Start Date (Opt)</Label>
                      <Input id="fetch-start-date" type="date" value={fetchStartDate} onChange={(e) => setFetchStartDate(e.target.value)} className="h-10 border-slate-200 focus:ring-sky-500 font-medium" />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="fetch-end-date" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Historical End Date (Opt)</Label>
                      <Input id="fetch-end-date" type="date" value={fetchEndDate} onChange={(e) => setFetchEndDate(e.target.value)} className="h-10 border-slate-200 focus:ring-sky-500 font-medium" />
                    </div>
                  </div>
                  <Button type="submit" disabled={fetchBusy} className={`w-full h-12 text-sm font-black tracking-wide shadow-md transition-all ${fetchBusy ? 'bg-slate-800' : 'bg-sky-600 hover:bg-sky-700'}`}>
                    {fetchBusy ? <span className="flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin"/> Executing Data Pipeline...</span> : "START NSE AUTO-SYNC"}
                  </Button>
                </form>

                {fetchResults && fetchResults.length > 0 && (
                  <div className="mt-6 bg-slate-900 rounded-xl p-4 shadow-inner">
                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-800 pb-2">Terminal Output Logs</h3>
                    <div className="max-h-48 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                      {fetchResults.map((res, i) => (
                        <div key={i} className={`p-2.5 rounded-md text-xs font-mono border ${res.status === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : res.status === 'skipped' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-rose-500/10 border-rose-500/30 text-rose-400'}`}>
                          <span className="font-bold opacity-75">[{res.date}]</span> {res.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* INDEX SYNC */}
            <Card className="p-0 overflow-hidden border-indigo-200 shadow-md bg-white rounded-2xl">
              <div className="bg-indigo-50 border-b border-indigo-100 p-4 flex items-center gap-3">
                 <ServerCrash className="w-5 h-5 text-indigo-600" />
                 <h2 className="text-lg font-black text-indigo-900 tracking-tight">Index Constituents Manager</h2>
              </div>
              <div className="p-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6 pb-6 border-b border-slate-100">
                  <p className="text-sm text-slate-600 font-medium">Sync live constituents for Nifty 50, Bank, IT, and 15+ more indices. Automatically purges outdated symbols.</p>
                  <Button onClick={handleAutoFetchIndices} disabled={autoIdxBusy} className="w-full md:w-auto h-10 bg-indigo-600 hover:bg-indigo-700 text-white font-bold shrink-0 shadow-md">
                    {autoIdxBusy ? <RefreshCw className="w-4 h-4 animate-spin mr-2"/> : <CloudDownload className="w-4 h-4 mr-2" />}
                    {autoIdxBusy ? "Syncing NSE..." : "Force Sync All Indices"}
                  </Button>
                </div>
                <div className="space-y-4">
                   <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Manual Index Fallback</h3>
                   <form onSubmit={handleIdx} className="flex flex-col md:flex-row gap-3">
                      <Input placeholder="Custom Index Name (e.g., MyTech)" value={idxName} onChange={(e) => setIdxName(e.target.value)} className="h-10 text-xs font-bold" />
                      <Input type="file" accept=".csv" onChange={(e) => setIdxFile(e.target.files?.[0] || null)} className="h-10 text-xs cursor-pointer" />
                      <Button type="submit" disabled={idxBusy} variant="outline" className="h-10 font-bold border-indigo-200 text-indigo-700 hover:bg-indigo-50 shrink-0">
                        {idxBusy ? "Processing..." : "Manual Import"}
                      </Button>
                   </form>
                   {idxResult && (
                      <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-xs font-bold text-indigo-800">
                        <FileCheck2 className="w-4 h-4 inline mr-1" /> {idxResult.index_name} — {idxResult.constituents_upserted} symbols updated.
                      </div>
                    )}
                </div>
              </div>
            </Card>
          </div>

          {/* 🔥 RIGHT COLUMN: MANUAL OVERRIDES 🔥 */}
          <div className="space-y-6">
            <Card className="p-0 overflow-hidden border-emerald-200 shadow-md bg-white rounded-2xl">
               <div className="bg-emerald-50 border-b border-emerald-100 p-4 flex items-center gap-3">
                 <UploadIcon className="w-5 h-5 text-emerald-600" />
                 <h2 className="text-lg font-black text-emerald-900 tracking-tight">Manual Bhavcopy CSV</h2>
               </div>
               <div className="p-6">
                 <p className="text-xs text-slate-500 mb-6 font-medium">For emergency use. Upload legacy NSE CSV files. Auto-detects date from filename (e.g. <code>pd12052026.csv</code>).</p>
                 <form onSubmit={handleCsv} className="space-y-5">
                   <div className="space-y-1.5">
                     <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Target File</Label>
                     <Input type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} className="h-10 text-xs cursor-pointer bg-slate-50" />
                   </div>
                   <div className="space-y-1.5">
                     <Label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Date Override (Optional)</Label>
                     <Input type="date" value={csvDate} onChange={(e) => setCsvDate(e.target.value)} className="h-10 font-medium" />
                   </div>
                   <Button type="submit" disabled={csvBusy} className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 font-bold shadow-md">
                     {csvBusy ? <RefreshCw className="w-4 h-4 animate-spin mr-2"/> : null}
                     {csvBusy ? "Running Pipeline..." : "Upload Raw Data"}
                   </Button>
                 </form>

                 {csvResult && (
                    <div className="mt-5 p-4 rounded-xl bg-slate-900 text-slate-300 text-xs font-mono shadow-inner border border-slate-800 space-y-1.5">
                      <div className="text-emerald-400 font-bold flex items-center gap-1.5 mb-2 border-b border-slate-700 pb-2"><FileCheck2 className="w-4 h-4" /> Upload Complete</div>
                      <div>Date: <span className="text-white font-bold">{csvResult.date}</span></div>
                      <div>Stocks: <span className="text-white font-bold">{csvResult.stocks_upserted}</span></div>
                      <div>Indices: <span className="text-white font-bold">{csvResult.indices_upserted}</span></div>
                      <div>Indicators Updated: <span className="text-sky-400 font-bold">{csvResult.indicators_updated}</span></div>
                    </div>
                  )}
               </div>
            </Card>
          </div>

        </div>
      </div>
    </div>
  );
}